"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StockLogo } from "@/components/stock-logo";
import { computePlayfulRates, type YearlyHistoryForecastBar, type DividendLineItem } from "@/lib/dividend-tracker/aggregation";
import { formatCurrency, formatPercent } from "@/lib/format";

function DetailRows({ items, currency }: { items: DividendLineItem[]; currency: string }) {
  const total = items.reduce((sum, i) => sum + i.amountNet, 0);
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item, i) => (
        <li
          key={`${item.symbol}-${i}`}
          className="relative flex items-center gap-3 overflow-hidden rounded-lg border border-hairline/60 py-2 pr-3 pl-2.5"
        >
          <div
            className="absolute inset-y-0 left-0 bg-gold/[0.08]"
            style={{ width: `${total > 0 ? Math.min(100, (item.amountNet / total) * 100) : 0}%` }}
          />
          <StockLogo symbol={item.symbol} logoUrl={item.logoUrl} size={24} className="relative z-10 shrink-0" />
          <span className="relative z-10 min-w-0 flex-1 truncate text-sm text-ink">
            {item.name}
            {item.confirmed === false && <span className="ml-1 text-muted-foreground">(ca.)</span>}
          </span>
          <span className="relative z-10 shrink-0 font-tabular text-sm text-ink">
            {formatCurrency(item.amountNet, currency)}
          </span>
          <span className="relative z-10 shrink-0 rounded-full bg-gold/15 px-2 py-0.5 font-tabular text-xs font-semibold text-gold-text">
            {formatPercent(total > 0 ? item.amountNet / total : 0, 0)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function RateTile({ label, value, currency }: { label: string; value: number; currency: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-tabular text-base text-ink">{formatCurrency(value, currency)}</div>
    </div>
  );
}

/**
 * Detail-Modal beim Klick auf einen Jahres-Balken (Feedback-Runde 13 Punkt 4,
 * 2026-07-22, Erik: "es soll sich ein Fenster öffnen wo man die Dividenden nochmal
 * deutlich detaillierter einsehen kann") - zusätzlich zum bestehenden
 * Klick-Verhalten (Balkenklick filtert weiterhin die "Alle Einträge"-Liste, siehe
 * `dividend-tracker-view.tsx`s `toggleYearFilter`), nicht als Ersatz dafür.
 *
 * "Detaillierter" als der Hover-Tooltip durch: Prozent-Anteil je Position (gleiches
 * Leaderboard-Zeilen-Muster wie `DepotBreakdownCard`) statt nur Logo+Betrag, und bei
 * einem reinen Zukunftsjahr (jenseits des laufenden Jahres, ohne bereits erhaltenen
 * Anteil) zusätzlich die Pro-Jahr/-Monat/-Tag/-Stunde-Kacheln (gleiches Muster wie die
 * bestehenden "letzte 365 Tage"-Kacheln in `dividend-tracker-view.tsx`, hier auf den
 * gewählten Prognosejahr-Betrag angewendet statt auf die rückblickenden 365 Tage).
 */
export function YearDetailDialog({
  bar,
  currency,
  currentYear,
  birthYear,
  open,
  onOpenChange,
}: {
  bar: YearlyHistoryForecastBar | null;
  currency: string;
  currentYear: number;
  /** Alters-Anzeige-Offset, siehe `DividendHistoryForecastChart`s `ageOffset`. */
  birthYear?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!bar) return null;

  const isPureForecastYear = bar.year > currentYear;
  const rates = isPureForecastYear && bar.forecastNet > 0 ? computePlayfulRates(bar.forecastNet) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{birthYear !== undefined ? `Alter ${bar.year - birthYear}` : bar.year}</DialogTitle>
          <DialogDescription>
            {formatCurrency(bar.actualNet + bar.forecastNet, currency)} netto insgesamt
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {bar.actualNet > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs tracking-wide text-muted-foreground uppercase">Erhalten</p>
                <p className="font-tabular text-sm text-ink">{formatCurrency(bar.actualNet, currency)}</p>
              </div>
              {bar.actualItems.length > 0 ? (
                <DetailRows items={bar.actualItems} currency={currency} />
              ) : (
                <p className="text-xs text-muted-foreground">Keine Einzelaufschlüsselung je Position verfügbar.</p>
              )}
            </div>
          )}

          {bar.forecastNet > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs tracking-wide text-muted-foreground uppercase">
                  {bar.actualNet > 0 ? "Noch erwartet" : "Prognose"}
                </p>
                <p className="font-tabular text-sm text-ink">{formatCurrency(bar.forecastNet, currency)}</p>
              </div>
              {bar.forecastItems.length > 0 ? (
                <DetailRows items={bar.forecastItems} currency={currency} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Hochgerechnete Depot-Summe für {bar.year}, ohne Aufschlüsselung je Position (Mehrjahres-Prognose
                  kennt nur die Jahressumme, nicht die einzelnen Positionen dahinter).
                </p>
              )}
            </div>
          )}

          {rates && (
            <div className="grid grid-cols-2 gap-3 border-t border-hairline pt-4 text-center sm:grid-cols-4">
              <RateTile label="Pro Jahr" value={rates.perYear} currency={currency} />
              <RateTile label="Pro Monat" value={rates.perMonth} currency={currency} />
              <RateTile label="Pro Tag" value={rates.perDay} currency={currency} />
              <RateTile label="Pro Stunde" value={rates.perHour} currency={currency} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
